import React, { useEffect, useState, useCallback } from "react";
import { fetchAllLeads, fetchAllUsers, API } from "../lib/api";
import { fetchEmployerPending, approveEmployer, rejectEmployer, setJobStatus, fetchAllEmployerJobs, setJobFeatured } from "../lib/employerApi";
import { useAuth } from "../lib/auth";
import { Download, X, ShieldAlert, Users as UsersIcon, ClipboardList, Building2, FileText, CheckCircle2, XCircle, Star, ExternalLink } from "lucide-react";
import GoogleButton from "./GoogleButton";

function toCsv(cols, header, rows) {
    const esc = (v) => {
        const s = (v == null ? "" : String(v)).replace(/"/g, '""');
        return `"${s}"`;
    };
    const out = [header.map(esc).join(",")];
    for (const r of rows) out.push(cols.map((c) => esc(r[c])).join(","));
    return out.join("\n");
}

function download(filename, csv) {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
}

function fmtDate(s) {
    if (!s) return "";
    return String(s).slice(0, 19).replace("T", " ");
}

export default function LeadsAdmin({ onClose }) {
    const { user, loading: authLoading, login } = useAuth();
    const [tab, setTab] = useState("leads");
    const [leads, setLeads] = useState([]);
    const [users, setUsers] = useState([]);
    const [pending, setPending] = useState({ employers: [], jobs: [] });
    const [approvedJobs, setApprovedJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [l, u, p, ap] = await Promise.all([
                fetchAllLeads(),
                fetchAllUsers(),
                fetchEmployerPending().catch(() => ({ employers: [], jobs: [] })),
                fetchAllEmployerJobs("approved").catch(() => []),
            ]);
            setLeads(l);
            setUsers(u);
            setPending(p);
            setApprovedJobs(ap);
            setError("");
        } catch {
            setError("Couldn't load admin data. Please check your admin access.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !user.is_admin) {
            setLoading(false);
            return;
        }
        load();
    }, [user, authLoading, load]);

    // Not signed in
    if (!authLoading && !user) {
        return (
            <div className="fixed inset-0 z-[600] bg-white flex items-center justify-center p-6" data-testid="leads-gate">
                <div className="max-w-md w-full text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ffdccb] text-[#e85812] flex items-center justify-center mb-4">
                        <ShieldAlert className="w-7 h-7" />
                    </div>
                    <h1 className="font-display font-extrabold text-2xl text-[#1a1e2c] mb-2">
                        Admin sign-in required
                    </h1>
                    <p className="text-slate-500 font-body text-[14px] mb-5">
                        The admin panel is restricted to Dailyworkjobs administrators.
                    </p>
                    <GoogleButton onClick={login} label="Continue with Google" />
                    <button className="mt-4 text-sm text-slate-500 hover:text-[#e85812] underline" onClick={onClose}>
                        Back to jobs
                    </button>
                </div>
            </div>
        );
    }

    // Signed in but not admin
    if (!authLoading && user && !user.is_admin) {
        return (
            <div className="fixed inset-0 z-[600] bg-white flex items-center justify-center p-6" data-testid="leads-gate-noadmin">
                <div className="max-w-md w-full text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mb-4">
                        <ShieldAlert className="w-7 h-7" />
                    </div>
                    <h1 className="font-display font-extrabold text-2xl text-[#1a1e2c] mb-2">
                        Access denied
                    </h1>
                    <p className="text-slate-500 font-body text-[14px] mb-5">
                        Your account (<b>{user.email}</b>) is not authorised to view the admin panel.
                    </p>
                    <button className="btn-ghost" onClick={onClose}>Back to jobs</button>
                </div>
            </div>
        );
    }

    const snagajobLeads = leads.filter((l) => l.src === "snagajob");
    const directLeads = leads.filter((l) => l.src !== "snagajob");

    const onDownload = () => {
        if (tab === "leads") {
            download(
                `dwj-leads-${new Date().toISOString().slice(0, 10)}.csv`,
                toCsv(
                    ["created_at", "name", "phone", "email", "consent_at", "job_title", "company", "city", "job_url"],
                    ["Date", "Name", "Phone", "Email", "ConsentAt", "Job", "Company", "City", "JobURL"],
                    directLeads,
                ),
            );
        } else if (tab === "snagajob") {
            download(
                `dwj-snagajob-applications-${new Date().toISOString().slice(0, 10)}.csv`,
                toCsv(
                    ["created_at", "name", "phone", "email", "job_title", "company", "city", "state", "job_url", "partner_application_id", "partner_seeker_id"],
                    ["Date", "Name", "Phone", "Email", "Job", "Company", "City", "State", "JobURL", "SnagajobAppID", "SnagajobSeekerID"],
                    snagajobLeads,
                ),
            );
        } else {
            download(
                `dwj-users-${new Date().toISOString().slice(0, 10)}.csv`,
                toCsv(
                    ["created_at", "name", "email", "last_login", "applies", "last_apply"],
                    ["SignedUpAt", "Name", "Email", "LastLogin", "Applies", "LastApply"],
                    users,
                ),
            );
        }
    };

    return (
        <div className="fixed inset-0 z-[600] bg-white overflow-auto" data-testid="leads-admin">
            <div className="max-w-6xl mx-auto p-5 md:p-8">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#1a1e2c]">
                            Admin panel
                        </h1>
                        <div className="text-sm text-slate-500 mt-0.5">
                            Signed in as <b>{user?.email}</b>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            className="btn-primary !py-2 !px-4"
                            onClick={onDownload}
                            data-testid="leads-download-csv"
                            disabled={
                                tab === "leads" ? !directLeads.length :
                                tab === "snagajob" ? !snagajobLeads.length :
                                tab === "users" ? !users.length : true
                            }
                        >
                            <Download className="w-4 h-4" /> CSV
                        </button>
                        <button className="btn-ghost !py-2 !px-3" onClick={onClose} data-testid="leads-close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-4 border-b border-slate-200">
                    <button
                        onClick={() => setTab("leads")}
                        className={`px-4 py-2.5 font-display font-semibold text-[14px] border-b-2 -mb-px flex items-center gap-2 transition-colors ${
                            tab === "leads"
                                ? "border-[#e85812] text-[#e85812]"
                                : "border-transparent text-slate-500 hover:text-[#1a1e2c]"
                        }`}
                        data-testid="tab-leads"
                    >
                        <ClipboardList className="w-4 h-4" />
                        Applications <span className="text-slate-400">({directLeads.length})</span>
                    </button>
                    <button
                        onClick={() => setTab("snagajob")}
                        className={`px-4 py-2.5 font-display font-semibold text-[14px] border-b-2 -mb-px flex items-center gap-2 transition-colors ${
                            tab === "snagajob"
                                ? "border-[#e85812] text-[#e85812]"
                                : "border-transparent text-slate-500 hover:text-[#1a1e2c]"
                        }`}
                        data-testid="tab-snagajob"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Snagajob <span className="text-slate-400">({snagajobLeads.length})</span>
                    </button>
                    <button
                        onClick={() => setTab("users")}
                        className={`px-4 py-2.5 font-display font-semibold text-[14px] border-b-2 -mb-px flex items-center gap-2 transition-colors ${
                            tab === "users"
                                ? "border-[#e85812] text-[#e85812]"
                                : "border-transparent text-slate-500 hover:text-[#1a1e2c]"
                        }`}
                        data-testid="tab-users"
                    >
                        <UsersIcon className="w-4 h-4" />
                        Signed-in users <span className="text-slate-400">({users.length})</span>
                    </button>
                    <button
                        onClick={() => setTab("employers")}
                        className={`px-4 py-2.5 font-display font-semibold text-[14px] border-b-2 -mb-px flex items-center gap-2 transition-colors ${
                            tab === "employers"
                                ? "border-[#e85812] text-[#e85812]"
                                : "border-transparent text-slate-500 hover:text-[#1a1e2c]"
                        }`}
                        data-testid="tab-employers"
                    >
                        <Building2 className="w-4 h-4" />
                        Employer moderation
                        <span className="text-slate-400">({(pending.employers?.length || 0) + (pending.jobs?.length || 0)})</span>
                    </button>
                </div>

                {loading && <div className="text-slate-500 text-sm">Loading…</div>}
                {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

                {tab === "leads" && (
                    <div className="card-soft overflow-hidden" data-testid="leads-table">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                    <tr className="text-left text-slate-500">
                                        <th className="p-3 font-display font-semibold">Date</th>
                                        <th className="p-3 font-display font-semibold">Name</th>
                                        <th className="p-3 font-display font-semibold">Phone</th>
                                        <th className="p-3 font-display font-semibold">Email</th>
                                        <th className="p-3 font-display font-semibold">Consent</th>
                                        <th className="p-3 font-display font-semibold">Job</th>
                                        <th className="p-3 font-display font-semibold">Company</th>
                                        <th className="p-3 font-display font-semibold">City</th>
                                        <th className="p-3 font-display font-semibold">Resume</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!loading && directLeads.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="p-6 text-center text-slate-500">
                                                No applications yet.
                                            </td>
                                        </tr>
                                    )}
                                    {directLeads.map((r) => (
                                        <tr
                                            key={`${r.email || "anon"}-${r.job_id || ""}-${r.created_at || Math.random()}`}
                                            className="border-b border-slate-100 hover:bg-slate-50"
                                        >
                                            <td className="p-3 whitespace-nowrap text-slate-600">{fmtDate(r.created_at)}</td>
                                            <td className="p-3 font-medium">{r.name}</td>
                                            <td className="p-3 text-slate-700">{r.phone}</td>
                                            <td className="p-3 text-slate-700">{r.email}</td>
                                            <td className="p-3 text-[11px] text-slate-500">{fmtDate(r.consent_at)}</td>
                                            <td className="p-3">{r.job_title}</td>
                                            <td className="p-3 text-slate-700">{r.company}</td>
                                            <td className="p-3 text-slate-700">{r.city}</td>
                                            <td className="p-3">
                                                {r.resume_path ? (
                                                    <a
                                                        href={`${API}/resumes/${encodeURI(r.resume_path)}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-1 text-[#e85812] text-xs font-display font-semibold underline"
                                                        data-testid="lead-resume-link"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" /> {r.resume_filename || "resume"}
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-300 text-xs">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "snagajob" && (
                    <div className="space-y-3" data-testid="snagajob-panel">
                        <div className="card-soft p-4 border-l-4 border-[#5a8bd6] bg-gradient-to-br from-[#eef4ff] to-white">
                            <div className="font-display font-bold text-[14px] text-[#1a1e2c] flex items-center gap-2">
                                <ExternalLink className="w-4 h-4 text-[#5a8bd6]" />
                                Snagajob Easy Apply — inbound applications
                            </div>
                            <div className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
                                Completed Snagajob applications post to
                                <code className="mx-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[11.5px]">/api/integrations/snagajob/apply</code>
                                and land here in real time. Configure the shared secret via
                                <code className="mx-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[11.5px]">SNAGAJOB_SHARED_SECRET</code>
                                once Snagajob issues it.
                            </div>
                        </div>

                        <div className="card-soft overflow-hidden" data-testid="snagajob-table">
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-100">
                                        <tr className="text-left text-slate-500">
                                            <th className="p-3 font-display font-semibold">Date</th>
                                            <th className="p-3 font-display font-semibold">Name</th>
                                            <th className="p-3 font-display font-semibold">Phone</th>
                                            <th className="p-3 font-display font-semibold">Email</th>
                                            <th className="p-3 font-display font-semibold">Job</th>
                                            <th className="p-3 font-display font-semibold">Company</th>
                                            <th className="p-3 font-display font-semibold">Location</th>
                                            <th className="p-3 font-display font-semibold">Snagajob IDs</th>
                                            <th className="p-3 font-display font-semibold">Link</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && snagajobLeads.length === 0 && (
                                            <tr>
                                                <td colSpan={9} className="p-6 text-center text-slate-500">
                                                    No Snagajob applications yet. Once you share the endpoint URL with Snagajob and they enable delivery, applications will land here.
                                                </td>
                                            </tr>
                                        )}
                                        {snagajobLeads.map((r) => (
                                            <tr
                                                key={r.lead_id || `${r.email}-${r.created_at}`}
                                                className="border-b border-slate-100 hover:bg-slate-50"
                                                data-testid={`snagajob-row-${r.lead_id || r.email}`}
                                            >
                                                <td className="p-3 whitespace-nowrap text-slate-600">{fmtDate(r.created_at)}</td>
                                                <td className="p-3 font-medium">{r.name}</td>
                                                <td className="p-3 text-slate-700">{r.phone || "—"}</td>
                                                <td className="p-3 text-slate-700">{r.email}</td>
                                                <td className="p-3">{r.job_title || "—"}</td>
                                                <td className="p-3 text-slate-700">{r.company || "—"}</td>
                                                <td className="p-3 text-slate-700">
                                                    {[r.city, r.state].filter(Boolean).join(", ") || "—"}
                                                </td>
                                                <td className="p-3 text-[11px] text-slate-500 leading-tight">
                                                    {r.partner_application_id ? (
                                                        <>App: <b className="text-slate-700">{r.partner_application_id}</b><br /></>
                                                    ) : null}
                                                    {r.partner_seeker_id ? (
                                                        <>Seeker: <b className="text-slate-700">{r.partner_seeker_id}</b></>
                                                    ) : null}
                                                    {!r.partner_application_id && !r.partner_seeker_id && "—"}
                                                </td>
                                                <td className="p-3">
                                                    {r.job_url ? (
                                                        <a
                                                            href={r.job_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-1 text-[#e85812] text-xs font-display font-semibold underline"
                                                        >
                                                            <ExternalLink className="w-3 h-3" /> Open
                                                        </a>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "users" && (
                    <div className="card-soft overflow-hidden" data-testid="users-table">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                    <tr className="text-left text-slate-500">
                                        <th className="p-3 font-display font-semibold">Signed up</th>
                                        <th className="p-3 font-display font-semibold">Name</th>
                                        <th className="p-3 font-display font-semibold">Email</th>
                                        <th className="p-3 font-display font-semibold">Last login</th>
                                        <th className="p-3 font-display font-semibold text-right">Applies</th>
                                        <th className="p-3 font-display font-semibold">Last apply</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!loading && users.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="p-6 text-center text-slate-500">
                                                No signed-in users yet. Once someone taps &quot;Continue with Google&quot; they&apos;ll appear here.
                                            </td>
                                        </tr>
                                    )}
                                    {users.map((u) => (
                                        <tr
                                            key={u.user_id || u.email}
                                            className="border-b border-slate-100 hover:bg-slate-50"
                                            data-testid={`user-row-${u.user_id || u.email}`}
                                        >
                                            <td className="p-3 whitespace-nowrap text-slate-600">{fmtDate(u.created_at)}</td>
                                            <td className="p-3 font-medium flex items-center gap-2">
                                                {u.picture ? (
                                                    <img
                                                        src={u.picture}
                                                        alt=""
                                                        className="w-7 h-7 rounded-full object-cover"
                                                        onError={(e) => (e.target.style.display = "none")}
                                                    />
                                                ) : (
                                                    <span className="w-7 h-7 rounded-full bg-[#ffdccb] text-[#e85812] flex items-center justify-center text-xs font-bold">
                                                        {(u.name || u.email || "?")[0]?.toUpperCase()}
                                                    </span>
                                                )}
                                                <span>{u.name || "—"}</span>
                                            </td>
                                            <td className="p-3 text-slate-700">{u.email}</td>
                                            <td className="p-3 text-slate-600 text-[12.5px]">{fmtDate(u.last_login)}</td>
                                            <td className="p-3 text-right font-display font-bold text-[#e85812]">{u.applies || 0}</td>
                                            <td className="p-3 text-slate-500 text-[12.5px]">{fmtDate(u.last_apply)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "employers" && (
                    <div className="space-y-5" data-testid="employers-panel">
                        <div className="card-soft p-4">
                            <div className="font-display font-bold text-[15px] text-[#1a1e2c] mb-2">
                                Pending employer accounts ({pending.employers?.length || 0})
                            </div>
                            {(pending.employers || []).length === 0 && (
                                <div className="text-slate-500 text-sm">No pending employer accounts.</div>
                            )}
                            <div className="space-y-2">
                                {(pending.employers || []).map((e) => (
                                    <div
                                        key={e.employer_id}
                                        className="border border-slate-100 rounded-2xl p-3 flex items-center gap-3 flex-wrap"
                                        data-testid={`pending-employer-${e.employer_id}`}
                                    >
                                        <div className="w-11 h-11 rounded-2xl bg-[#ffdccb] text-[#e85812] flex items-center justify-center shrink-0">
                                            <Building2 className="w-5 h-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display font-semibold text-[14px] text-[#1a1e2c] truncate">{e.company_name}</div>
                                            <div className="text-[12px] text-slate-500 truncate">{e.email} · {e.company_domain}</div>
                                        </div>
                                        <button
                                            className="btn-primary !py-1.5 !px-3 !text-xs"
                                            onClick={async () => { await approveEmployer(e.employer_id); await load(); }}
                                            data-testid={`approve-employer-${e.employer_id}`}
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                        </button>
                                        <button
                                            className="btn-ghost !py-1.5 !px-3 !text-xs text-red-500"
                                            onClick={async () => { await rejectEmployer(e.employer_id); await load(); }}
                                            data-testid={`reject-employer-${e.employer_id}`}
                                        >
                                            <XCircle className="w-3.5 h-3.5" /> Reject
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="card-soft p-4">
                            <div className="font-display font-bold text-[15px] text-[#1a1e2c] mb-2">
                                Pending job postings ({pending.jobs?.length || 0})
                            </div>
                            {(pending.jobs || []).length === 0 && (
                                <div className="text-slate-500 text-sm">No pending job postings.</div>
                            )}
                            <div className="space-y-2">
                                {(pending.jobs || []).map((j) => (
                                    <div
                                        key={j.job_id}
                                        className="border border-slate-100 rounded-2xl p-3 flex items-center gap-3 flex-wrap"
                                        data-testid={`pending-job-${j.job_id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display font-semibold text-[14px] text-[#1a1e2c] truncate">{j.title}</div>
                                            <div className="text-[12px] text-slate-500 truncate">
                                                {j.company} · {j.loc || j.city} {j.pay ? ` · ${j.pay}` : ""}
                                            </div>
                                            <a href={j.url} target="_blank" rel="noreferrer" className="text-[11.5px] text-[#e85812] underline break-all">
                                                {j.url}
                                            </a>
                                        </div>
                                        <button
                                            className="btn-primary !py-1.5 !px-3 !text-xs"
                                            onClick={async () => { await setJobStatus(j.job_id, "approved"); await load(); }}
                                            data-testid={`approve-job-${j.job_id}`}
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                        </button>
                                        <button
                                            className="btn-ghost !py-1.5 !px-3 !text-xs text-red-500"
                                            onClick={async () => { await setJobStatus(j.job_id, "rejected"); await load(); }}
                                            data-testid={`reject-job-${j.job_id}`}
                                        >
                                            <XCircle className="w-3.5 h-3.5" /> Reject
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="card-soft p-4">
                            <div className="font-display font-bold text-[15px] text-[#1a1e2c] mb-2 flex items-center gap-2">
                                <Star className="w-4 h-4 text-[#b8890c] fill-[#f5c542]" />
                                Approved jobs — toggle featured ({approvedJobs.length})
                            </div>
                            <div className="text-[12px] text-slate-500 mb-3">
                                Employers email <b>Hello@downtownmango.com</b> to request a featured slot. Approve here to give them a golden pin.
                            </div>
                            {approvedJobs.length === 0 && (
                                <div className="text-slate-500 text-sm">No approved employer jobs yet.</div>
                            )}
                            <div className="space-y-2">
                                {approvedJobs.map((j) => (
                                    <div
                                        key={j.job_id}
                                        className={`border rounded-2xl p-3 flex items-center gap-3 flex-wrap ${j.featured ? "border-[#f5c542] bg-gradient-to-r from-[#fff9dd] to-white" : "border-slate-100"}`}
                                        data-testid={`approved-job-${j.job_id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display font-semibold text-[14px] text-[#1a1e2c] truncate flex items-center gap-1.5">
                                                {j.featured && <Star className="w-3.5 h-3.5 text-[#b8890c] fill-[#f5c542] shrink-0" />}
                                                {j.title}
                                            </div>
                                            <div className="text-[12px] text-slate-500 truncate">
                                                {j.company} · {j.loc || j.city} {j.pay ? ` · ${j.pay}` : ""}
                                            </div>
                                        </div>
                                        <button
                                            className={j.featured ? "btn-ghost !py-1.5 !px-3 !text-xs !text-[#b8890c] !border-[#f5c542] !bg-[#fff3b8]" : "btn-primary !py-1.5 !px-3 !text-xs"}
                                            onClick={async () => { await setJobFeatured(j.job_id, !j.featured); await load(); }}
                                            data-testid={`toggle-featured-${j.job_id}`}
                                        >
                                            <Star className={`w-3.5 h-3.5 ${j.featured ? "fill-[#b8890c]" : "fill-white"}`} />
                                            {j.featured ? "Un-feature" : "Feature"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
